import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router";
import Button from "../../../components/common/button/Button";
import Table from "../../../components/common/table/Table";
import { routeNames } from "../../../consts/routesNames";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import "./DisplayContacts.css";
import { createMenuItem } from "../../../helpers/menuHelper";
import { useDispatch } from "react-redux";
import { openModal } from "../../../redux/modal/modalActions";
import ConfirmationBox from "../../../components/common/confirmationBox/ConfirmationBox";
import { navtabs } from "../../../consts/navTabs";
import { setActiveNavTab } from "../../../redux/navbar/navbarActions";
import { getAllContacts } from "../../../apis/contactsApis/getAllContacts";
import { deleteContact } from "../../../apis/contactsApis/deleteContact";
import ResponseModal from "../../../components/common/modal/responseModal/ResponseModal";
import { PERMISSIONS } from "../../../auth/utils/permissionCodes";
import { useAuth } from "../../../auth/hooks/usePermissions";
import { useCrudPermissions } from "../../../auth/hooks/useCrudPermissions";
import { useUpdateOwnership } from "../../../auth/hooks/useUpdateOwnership";
import { mapContactLinkedUserFields } from "../../../auth/utils/contactLink";
import { emptyTableMessageForLinkedContact, LINKED_CONTACT_TABLE_EMPTY } from "../../../consts/tableEmptyMessages";
import { formatCreatedByDisplay } from "../../../utils/tableDisplayUtils";

function DisplayContacts({ homePageDisplay }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    canCreate: canCreateContacts,
    canUpdate: canUpdateContacts,
    canDelete: canDeleteContacts,
  } = useCrudPermissions(PERMISSIONS.CONTACTS);
  const { canUpdateOwnedRecord } = useUpdateOwnership();
  const { contactId, isAdmin } = useAuth();

  const [contactsData, setContactsData] = useState([]);

  const contactsEmptyMessage = emptyTableMessageForLinkedContact(contactId, contactsData.length, LINKED_CONTACT_TABLE_EMPTY.CONTACTS);

  useEffect(() => {
    if (location.pathname === routeNames.CONTACTS) {
      dispatch(setActiveNavTab(navtabs.CONTACTS));
    }
  }, [location.pathname, dispatch]);

  // === Getting Table Data  ===
  const getContacts = useCallback(async () => {
    try {
      const allContactsRes = await getAllContacts();
      if (allContactsRes.error_code === 2 || allContactsRes.error_code === 3) {
        dispatch(openModal(<ResponseModal text={allContactsRes.error_message} isError={true} />));
      } else {
        let contactsArray = [];
        const list = allContactsRes.res?.data || [];
        if (list.length > 0) {
          contactsArray = list.map((contact) => ({
            ...contact,
            full_name: `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim(),
            company_name: contact.company?.name ?? "",
            category_name: contact.category?.name ?? "",
            created_by_display: formatCreatedByDisplay(contact.created_by),
            ...mapContactLinkedUserFields(contact),
          }));
        }
        setContactsData(contactsArray);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    getContacts();
  }, [getContacts]);

  const tableHeaders = useMemo(() => {
    const headers = [
      { key: "full_name", name: "Name" },
      { key: "company_name", name: "Company Name" },
      { key: "phone_number", name: "Phone Number" },
      { key: "email", name: "Email" },
      { key: "category_name", name: "Category" },
    ];
    if (isAdmin) {
      headers.push({ key: "linked_user_name", name: "Linked User" });
    }
    headers.push({ key: "created_by_display", name: "Created By" });
    return headers;
  }, [isAdmin]);
  // === Click Button Action Handlers ===
  const HandleContactDetailsClick = (rowData) => {
    navigate(`${routeNames.CONTACT_DETAILS}?id=${rowData.id}`, { state: { data: rowData } });
  };

  const HandleAddNewContactClick = () => {
    if (!canCreateContacts) return;
    navigate(routeNames.ADD_CONTACT);
  };

  // === Delete Contact Action ===
  const confirmDeleteContact = (rowData) => {
    if (!canDeleteContacts) return;
    dispatch(
      openModal(
        <ConfirmationBox
          msg={`Are you sure you want to delete "${rowData.first_name} ${rowData.last_name}"`}
          saveText={"OK"}
          discardText={"Cancel"}
          saveAction={async () => {
            try {
              await deleteContact(rowData.id);
              errorDispatcher("Deleted Successfully");
              getContacts();
            } catch (error) {
              console.error("Error deleting contact:", error);
              errorDispatcher("Failed to delete the contact.");
            }
          }}
        />,
      ),
    );
  };

  const errorDispatcher = (message) => {
    alert(message);
    dispatch(openModal(<ResponseModal text={message} isError={true} />));
  };

  // === Edit Contact Action ===
  const canEditContact = (rowData) =>
    canUpdateOwnedRecord(rowData.created_by, canUpdateContacts);

  const editContact = (rowData) => {
    if (!canEditContact(rowData)) return;
    navigate(`${routeNames.EDIT_CONTACT.split(":")[0]}${rowData.id}`, {
      state: { data: rowData },
    });
  };

  const getContactRowActions = (row) =>
    [
      canEditContact(row) && createMenuItem("Edit", editContact, <EditIcon />),
      canDeleteContacts &&
        createMenuItem("Delete", confirmDeleteContact, <DeleteIcon />),
    ].filter(Boolean);

  return (
    <>
      <div className={homePageDisplay ? "main-container-displayContacts-homePageDisplay" : "main-container-displayContacts"}>
        {!homePageDisplay ? (
          <div className="btns-main-container-displayContacts">
            {canCreateContacts && (
              <div className="add-btn-container-displayContacts">
                <Button className={"add-btn-displayContacts"} buttonText={"Add New Contact"} onClick={HandleAddNewContactClick} />
              </div>
            )}
          </div>
        ) : (
          <></>
        )}

        <div className="table-main-container-displayContacts">
          <Table
            tableHeight={homePageDisplay ? "450px" : "650px"}
            headers={tableHeaders}
            rows={contactsData}
            searchable={homePageDisplay ? false : true}
            onRowClick={HandleContactDetailsClick}
            getRowActions={getContactRowActions}
            emptyMessage={contactsEmptyMessage}
          />
        </div>
      </div>
    </>
  );
}

export default DisplayContacts;
